import {
  useState,
  useEffect,
  FunctionComponent,
  SyntheticEvent,
  memo,
  SetStateAction,
  Dispatch,
} from 'react';

import { useToggle } from 'react-use';
import _ from 'lodash';
import { Button, Price } from '~ui/index';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { Loader } from '../Loader';
import { SteamItemImage, GetName } from '../item';
import playAudio from '~frontend/utils/playAudio';
import Currency from '../Currency';
import { Game as TGame } from '~types';
import IconEmpty from '../../scss/images/icon-profile-empty.svg';
import {
  useItemsInContract,
  useIdsItemsInContract,
  useContractAmount,
} from './hooks';
import { useWinGame } from '../wheel/hooks';
import { useActiveItemsUser, useProfile } from '../hooks';
import { Slider } from 'antd';
import { useConfig } from '~components/hooks';

interface ItemsUserProps {
  filterOrder: boolean;
  filterSearch: string;
  sortField: string;
  minPrice: number;
  maxPrice: number;
  userItems: TGame[];
  setUserItems: Dispatch<SetStateAction<TGame[]>>;
}

const onChange = (value: number | [number, number]) => {
  console.log('onChange: ', value);
};

const onAfterChange = (value: number | [number, number]) => {
  console.log('onAfterChange: ', value);
};

const ItemsUser: FunctionComponent<ItemsUserProps> = memo(
  ({
    filterOrder,
    filterSearch,
    sortField,
    minPrice,
    maxPrice,
    userItems,
    setUserItems,
  }) => {
    const { t } = useTranslation();
    const getProfile = useProfile();
    const getConfig = useConfig();
    const [itemsInContract, setItemsInContract] = useItemsInContract();
    const [contractAmount, setContractAmount] = useContractAmount();
    const [idsItemsInContract] = useIdsItemsInContract();

    const { loading, error, data, fetchMore, variables, networkStatus } =
      useActiveItemsUser({
        notifyOnNetworkStatusChange: true,
        variables: {
          input: {
            limit: 15,
            offset: 0,
            userId: getProfile?.id,
            filterSearch,
            filterOrder: {
              sortField,
              sortOrder: filterOrder ? 'desc' : 'asc',
            },
            minPrice,
            maxPrice,
          },
        },
      });

    const getActiveHistory = data?.getActiveHistory;

    const { state } = useLocation();

    const addItemsToContract = (gameIds: number[]) => {
      const getItems = [];
      let getPice = 0;

      for (const gameId of gameIds) {
        const getGame = _.clone(_.find(userItems, ['id', gameId]));
        if (!getGame) {
          continue;
        }

        getPice += getGame.price;

        getItems.push(_.update(getGame, 'animate', () => true));
      }

      setItemsInContract(getItems);
      setContractAmount(getPice);
    };

    useEffect(() => {
      if (_.get(state, 'gameIds') && _.size(userItems) > 0) {
        addItemsToContract(_.get(state, 'gameIds'));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, userItems]);

    useEffect(
      () => setUserItems(_.toArray(data?.getActiveHistory?.getGames)),
      [data?.getActiveHistory?.getGames],
    );

    if (loading && _.includes([2], networkStatus)) {
      return <Loader />;
    }

    if (error) {
      return <>ItemsUser | Error! {error.message}</>;
    }

    const removeItemFromContract = (event: SyntheticEvent<HTMLDivElement>) => {
      event.preventDefault();

      const gameId = Number(event.currentTarget.dataset.id);

      const getGame = _.clone(_.find(itemsInContract, ['id', gameId]));

      setItemsInContract(
        _.filter(itemsInContract, getItem => getItem.id !== gameId),
      );

      setContractAmount(contractAmount - getGame.price);
      playAudio(null, '14.mp3', 0.4);
    };

    const addItemToContract = (
      event: SyntheticEvent<HTMLDivElement>,
      { id } = event.currentTarget.dataset,
    ) => {
      if (event) {
        event.preventDefault();
      }

      const gameId = Number(id);

      if (idsItemsInContract.includes(gameId)) {
        removeItemFromContract(event);
        return;
      }

      if (idsItemsInContract.length >= 10) {
        return;
      }

      const getGame = _.clone(_.find(userItems, ['id', gameId]));

      if (!getGame) {
        return;
      }

      setItemsInContract(
        _.concat(
          itemsInContract,
          _.update(getGame, 'animate', () => true),
        ),
      );
      setContractAmount(contractAmount + getGame.price);
      playAudio(null, '12.mp3', 0.4);
    };

    const onLoadMore = async () => {
      try {
        const {
          data: { getActiveHistory },
        } = await fetchMore({
          variables: _.merge({}, variables, {
            input: {
              offset: userItems?.length,
            },
          }),
        });

        setUserItems(_.uniq(_.concat(userItems, getActiveHistory?.getGames)));
      } catch {
        //
      }
    };

    if (getActiveHistory?.count === 0) {
      return (
        <div className="empty">
          <img src={IconEmpty} alt="" />
          <div className="text">
            {t('Its time to beat out the top skins!')}
            <br />
            {t('For this, only')}
          </div>
          <Link className="btn btn-violet" to="/">
            {t('Open the case')}
          </Link>
        </div>
      );
    }

    return (
      <>
        <div className={`in-case-wrap`}>
          {userItems.map(getGame => (
            <div
              data-active={idsItemsInContract.includes(getGame.id)}
              className="in-case-item status-1"
              data-rar={getGame.getItem.color}
              onMouseEnter={playAudio}
              data-audio="5.wav"
              data-volume="0.4"
              key={getGame.id}
              onClick={addItemToContract}
              data-id={getGame.id}
            >
              <div className="in-case-cost">
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    <Price sum={getGame.price} />
                  </>
                ) : (
                  <>
                    <Price sum={getGame.price} />
                    <Currency />
                  </>
                )}
              </div>
              {idsItemsInContract.includes(getGame.id) && (
                <div className="in-case-status-default">
                  <div className="in-case-status-default-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                    >
                      <g transform="translate(-1579 -1363)">
                        <g
                          transform="translate(1579 1363)"
                          fill="#fff"
                          stroke="#707070"
                          strokeWidth="1"
                          opacity="0"
                        >
                          <rect width="16" height="16" stroke="none"></rect>
                          <rect
                            x="0.5"
                            y="0.5"
                            width="15"
                            height="15"
                            fill="none"
                          ></rect>
                        </g>
                        <path
                          d="M4,9H6V5H8V9h2L7,12ZM2,7V3H0L3,0,6,3H4V7Z"
                          transform="translate(1582 1365)"
                          fill="#fff"
                        ></path>
                      </g>
                    </svg>
                  </div>
                </div>
              )}
              <div className="in-case-item-wrap">
                <div className="img">
                  <div className="in-case-item-bg" />
                  {idsItemsInContract.includes(getGame.id) && (
                    <div className="img-contracts">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                      >
                        <g transform="translate(1611 1395) rotate(180)">
                          <g
                            transform="translate(1579 1363)"
                            fill="#8847ff"
                            stroke="#707070"
                            strokeWidth="1"
                            opacity="0"
                          >
                            <rect width="32" height="32" stroke="none"></rect>
                            <rect
                              x="0.5"
                              y="0.5"
                              width="31"
                              height="31"
                              fill="none"
                            ></rect>
                          </g>
                          <path
                            d="M-64,4478h-2v-2a9.871,9.871,0,0,1-6.2-2.8A9.869,9.869,0,0,1-75,4467h-2v-2h2a9.869,9.869,0,0,1,2.8-6.2A9.875,9.875,0,0,1-66,4456v-2h2v2a9.876,9.876,0,0,1,6.2,2.8A9.868,9.868,0,0,1-55,4465h2v2h-2a9.872,9.872,0,0,1-2.8,6.2A9.876,9.876,0,0,1-64,4476v2Zm0-5v1a7.509,7.509,0,0,0,7-7h-1v-2h1a7.51,7.51,0,0,0-7-7v1h-2v-1a7.507,7.507,0,0,0-7,7h1v2h-1a7.5,7.5,0,0,0,7,7v-1Zm-1-2a5.2,5.2,0,0,1-5-5,5.2,5.2,0,0,1,5-5,5.2,5.2,0,0,1,5,5A5.2,5.2,0,0,1-65,4471Zm0-8a3,3,0,0,0-3,3,3,3,0,0,0,3,3,3,3,0,0,0,3-3A3,3,0,0,0-65,4463Z"
                            transform="translate(1660 -3087)"
                            fill="#8847ff"
                            stroke="rgba(0,0,0,0)"
                            strokeMiterlimit="10"
                            strokeWidth="1"
                          ></path>
                        </g>
                      </svg>
                      <span>{t('In the contract')}</span>
                    </div>
                  )}
                  <SteamItemImage
                    url={getGame.getItem.getImage}
                    size="350x262"
                  />
                </div>
                <div className="in-case-info">
                  <div className="in-case-item-name">
                    <GetName getItem={getGame.getItem} type={0} />
                  </div>
                  <div className="in-case-item-desc">
                    <GetName getItem={getGame.getItem} type={1} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {userItems.length > 0 &&
          getActiveHistory?.count > userItems?.length && (
            <Button
              loading={loading}
              className="btn btn-blue btn-load"
              onClick={onLoadMore}
            >
              <>
                {t('Show more')}
                <i className="fas fa-redo-alt"></i>
              </>
            </Button>
          )}
      </>
    );
  },
);

const ItemsUserView: FunctionComponent = () => {
  const [minPrice] = useState(0);
  const [maxPrice] = useState(100000000);
  const [winGame] = useWinGame();
  const [sortField, setSortField] = useState('id');
  const [filterOrder, setFilterOrder] = useToggle(true);
  const [filterSearch, setFilterSearch] = useState('');
  const [userItems, setUserItems] = useState([]);
  const { t } = useTranslation();

  const onSetFilterOrder = (event: SyntheticEvent) => {
    event.preventDefault();

    setSortField('price');

    setFilterOrder();
  };

  const onSetFilterSearch = (
    event: SyntheticEvent<HTMLInputElement | HTMLAnchorElement>,
  ) => {
    event.preventDefault();

    const target = event.target as Element & HTMLInputElement;

    const el = target.parentElement;

    setFilterSearch(
      event.type === 'click' ? el.querySelector('input').value : target.value,
    );
  };

  return (
    <div
      className="profile-cases-wrap"
      style={{ pointerEvents: winGame ? 'none' : 'auto' }}
    >
      <div className="in-case profile-cases profile-cases-user">
        <div className="in-case-title">
          <div className="text">{t('Your items')}</div>
          {userItems.length > 0 && (
            <>
              <div className="search-block">
                <input
                  type="text"
                  placeholder={t('Quick search')}
                  value={filterSearch}
                  onChange={onSetFilterSearch}
                />
                <a className="btn-search" href="#" onClick={onSetFilterSearch}>
                  <span className="material-icons">search</span>
                </a>
              </div>
              {/* <div className="range-slider">
            <div className="range-slider-inputs">
              <input type="text" value={'$1'} />
              <span />
              <input type="text" value={'$100'} />
            </div>
            <Slider
              range
              step={1}
              defaultValue={[minPrice, maxPrice]}
              onChange={onChange}
              onAfterChange={onAfterChange}
            />
          </div> */}
              <a
                className={`in-case-sort ${filterOrder && 'active'}`}
                href="#"
                onClick={onSetFilterOrder}
              >
                <span className="icon material-icons">attach_money</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="42"
                  height="42"
                  viewBox="0 0 42 42"
                >
                  <path
                    d="M1992,1385.667l16-16h-8V1367h13.333v13.333h-2.667v-8l-16,16Z"
                    transform="translate(-1981.333 -1356.333)"
                    fill="#3B1674"
                  ></path>
                </svg>
              </a>
            </>
          )}
        </div>
        <div className="contract-items-content">
          <ItemsUser
            filterOrder={filterOrder}
            filterSearch={filterSearch}
            sortField={sortField}
            minPrice={minPrice}
            maxPrice={maxPrice}
            userItems={userItems}
            setUserItems={setUserItems}
          />
        </div>
      </div>
    </div>
  );
};

export default ItemsUserView;
